import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { SignInPage } from "./pages/signInPage/SignInPage";
import { SignUpPage } from "./pages/signUpPage/SignUpPage";
import { Container } from "./components/container/Container";
import { tailChase } from "ldrs";
import "./assets/common.sass";
import { PrivateRoute } from "./components/privateRoute/PrivateRoute";
import { ConditionalRoute } from "./components/privateRoute/ConditionalRoute";
import { ROUTE_URLS } from "./constants/consts";
import { NotFoundPage } from "./pages/notFoundPage/NotFoundPage";
import { useAppSelector } from "./hooks/ReduxHooks";
import usePaginationScroll from "./hooks/UsePaginationScroll";
import { useUnauthorizedLangFromUrl } from "./hooks/useUnauthorizedLangFromUrl.hook";
import { RecoverPasswordPage } from "./pages/RecoverPasswordPage/RecoverPasswordPage";
import { RecoverPasswordFormPage } from "./pages/RecoverPasswordFormPage/RecoverPasswordFormPage";
import { LandingPage } from "./pages/landingPage/LandingPage";
import { useBlockedAccess } from "./hooks/useBlockedAccess.hook";
import { BlockedPage } from "./pages/blockedPage/BlockedPage";
import { AppSeo } from "./components/seo/AppSeo";
import { MaintenancePage } from "./pages/maintenancePage/MaintenancePage";
tailChase.register();

const HomePage = lazy(() =>
  import("./pages/homePage/HomePage").then((module) => ({
    default: module.HomePage,
  }))
);
const ProductCatalogPage = lazy(() =>
  import("./pages/productCatalogPage/productCatalogPage").then((module) => ({
    default: module.ProductCatalogPage,
  }))
);
const SearchForm = lazy(() =>
  import("./components/search/SearchForm").then((module) => ({
    default: module.SearchForm,
  }))
);
const CartPage = lazy(() =>
  import("./pages/cartPage/CartPage").then((module) => ({
    default: module.CartPage,
  }))
);
const ProductPage = lazy(() =>
  import("./pages/productPage/ProductPage").then((module) => ({
    default: module.ProductPage,
  }))
);
const CreditLine = lazy(() =>
  import("./components/creditLine/creditLine/CreditLine").then((module) => ({
    default: module.CreditLine,
  }))
);
const OrdersPage = lazy(() =>
  import("./pages/ordersPage/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  }))
);
const OrderPage = lazy(() =>
  import("./pages/orderPage/OrderPage").then((module) => ({
    default: module.OrderPage,
  }))
);
const QuestionaryPage = lazy(() =>
  import("./pages/questionaryPage/QuestionaryPage").then((module) => ({
    default: module.QuestionaryPage,
  }))
);
const ProductReturnsPage = lazy(() =>
  import("./pages/productReturnsPage/ProductReturnsPage").then((module) => ({
    default: module.ProductReturnsPage,
  }))
);
const CreditLineHistory = lazy(() =>
  import("./components/creditLine/creditLineHistory/CreditLineHistory").then((module) => ({
    default: module.CreditLineHistory,
  }))
);
const TermService = lazy(() =>
  import("./pages/termService/TermService").then((module) => ({
    default: module.TermService,
  }))
);
const PortalRulesPage = lazy(() =>
  import("./pages/portalRulesPage/PortalRulesPage").then((module) => ({
    default: module.PortalRulesPage,
  }))
);
const ContactInformation = lazy(() =>
  import("./pages/contactInformation/ContactInformation").then((module) => ({
    default: module.ContactInformation,
  }))
);
const ReturnPage = lazy(() =>
  import("./pages/returnPage/ReturnPage").then((module) => ({
    default: module.ReturnPage,
  }))
);
const NewCreditLine = lazy(() =>
  import("./components/creditLine/creditLine/NewCreditLine").then((module) => ({
    default: module.NewCreditLine,
  }))
);
const WarrantyRequest = lazy(() =>
  import("./pages/warrantyRequest/WarrantyRequest").then((module) => ({
    default: module.WarrantyRequest,
  }))
);
const TwoFactorAuthPage = lazy(() =>
  import("./pages/twoFactorAuthPage/TwoFactorAuthPage").then((module) => ({
    default: module.TwoFactorAuthPage,
  }))
);
const WarrantyRequestPage = lazy(() =>
  import("./pages/warrantyRequestPage/WarrantyRequestPage").then((module) => ({
    default: module.WarrantyRequestPage,
  }))
);
const WarrantyTermsPage = lazy(() =>
  import("./pages/warrantyTermsPage/WarrantyTermsPage").then((module) => ({
    default: module.WarrantyTermsPage,
  }))
);
const WarrantyPeriodsPage = lazy(() =>
  import("./pages/warrantyPeriodsPage/WarrantyPeriodsPage").then((module) => ({
    default: module.WarrantyPeriodsPage,
  }))
);
const WarrantyCasePage = lazy(() =>
  import("./pages/warrantyCasePage/WarrantyCasePage").then((module) => ({
    default: module.WarrantyCasePage,
  }))
);
const ShippingQuotePage = lazy(() =>
  import("./pages/shippingQuotePage").then((module) => ({
    default: module.ShippingQuotePage,
  }))
);
const ShippingRequestPage = lazy(() =>
  import("./pages/shippingQuotePage/shippingRequestPage").then((module) => ({
    default: module.ShippingRequestPage,
  }))
);
const InvoicesPage = lazy(() =>
  import("./pages/invoicesPage/invoicesPage").then((module) => ({
    default: module.InvoicesPage,
  }))
);
const ImagePage = lazy(() =>
  import("./pages/imagePage/ImagePage").then((module) => ({
    default: module.ImagePage,
  }))
);

function App() {
  const { page, displayType } = useAppSelector((state) => state.page);
  usePaginationScroll(page, displayType);
  useUnauthorizedLangFromUrl();
  const {
    isBlocked,
    isMaintenance,
    blockPageText,
    blockEndDate,
    isLoading,
  } = useBlockedAccess();

  if (isLoading) {
    return null;
  }

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  if (isBlocked && blockPageText) {
    return <BlockedPage text={blockPageText} endDate={blockEndDate} />;
  }

  return (
    <Container>
      <Suspense fallback={null}>
        <AppSeo />
        <Routes>
          <Route path={ROUTE_URLS.SIGN_IN} element={<SignInPage />} />
          <Route path={ROUTE_URLS.SIGN_UP} element={<SignUpPage />} />
          <Route
            path={ROUTE_URLS.RECOVER_PASSWORD}
            element={<RecoverPasswordPage />}
          />
          <Route
            path={ROUTE_URLS.RECOVER_PASSWORD_FORM}
            element={<RecoverPasswordFormPage />}
          />
          <Route path={ROUTE_URLS.LANDING_PAGE} element={<LandingPage />} />
          <Route
            path={ROUTE_URLS.SHIPPING_QUOTE_PAGE}
            element={<ConditionalRoute />}
          >
            <Route path="" element={<ShippingQuotePage />} />
          </Route>
          <Route
            path={ROUTE_URLS.SHIPPING_REQUEST}
            element={<ConditionalRoute />}
          >
            <Route path="" element={<ShippingRequestPage />} />
          </Route>
          <Route path={ROUTE_URLS.IMAGE} element={<ImagePage />} />
          <Route
            path={ROUTE_URLS.TWO_FACTOR_AUTH}
            element={<PrivateRoute />}
          >
            <Route path="" element={<TwoFactorAuthPage />} />
          </Route>
          <Route path={ROUTE_URLS.HOME} element={<PrivateRoute />}>
            <Route path="" element={<HomePage />} />
          </Route>
          <Route path={ROUTE_URLS.CART} element={<PrivateRoute />}>
            <Route path="" element={<CartPage />} />
          </Route>
          <Route path={ROUTE_URLS.CATALOG_CATEGORY} element={<PrivateRoute />}>
            <Route path="" element={<ProductCatalogPage />} />
          </Route>
          <Route path={ROUTE_URLS.CATALOG} element={<PrivateRoute />}>
            <Route path="" element={<ProductCatalogPage />} />
          </Route>

          <Route
            path={ROUTE_URLS.PRODUCT_FROM_CATEGORY}
            element={<PrivateRoute />}
          >
            <Route path="" element={<ProductPage />} />
          </Route>
          <Route path={ROUTE_URLS.PRODUCT} element={<PrivateRoute />}>
            <Route path="" element={<ProductPage />} />
          </Route>
          <Route path={ROUTE_URLS.ORDERS} element={<PrivateRoute />}>
            <Route path="" element={<OrdersPage />} />
          </Route>
          <Route path={ROUTE_URLS.ORDER} element={<PrivateRoute />}>
            <Route path="" element={<OrderPage />} />
          </Route>
          <Route path={ROUTE_URLS.RETURNS} element={<PrivateRoute />}>
            <Route path="" element={<ProductReturnsPage />} />
          </Route>
          <Route path={ROUTE_URLS.RETURN} element={<PrivateRoute />}>
            <Route path="" element={<ReturnPage />} />
          </Route>
          {/* Temporarily hidden */}
          {/* <Route path={ROUTE_URLS.FIN_TERMS} element={<PrivateRoute />}>
            <Route path="" element={<FinTermsPage />} />
          </Route> */}
          <Route path={ROUTE_URLS.INVOICES} element={<PrivateRoute />}>
            <Route path="" element={<InvoicesPage />} />
          </Route>
          <Route path={ROUTE_URLS.NEW_CREDIT_LIMIT} element={<PrivateRoute />}>
            <Route path="" element={<NewCreditLine />} />
          </Route>
          <Route path={ROUTE_URLS.CREDIT_LIMIT} element={<PrivateRoute />}>
            <Route path="" element={<CreditLine />} />
          </Route>
          <Route
            path={ROUTE_URLS.CREDIT_LIMIT_HISTORY}
            element={<PrivateRoute />}
          >
            <Route path="" element={<CreditLineHistory />} />
          </Route>
          <Route path="/search" element={<PrivateRoute />}>
            <Route path="" element={<SearchForm />} />
          </Route>

          <Route path={ROUTE_URLS.QUESTIONARY} element={<PrivateRoute />}>
            <Route path="" element={<QuestionaryPage />} />
          </Route>
          <Route path={ROUTE_URLS.TERM_SERVICE} element={<PrivateRoute />}>
            <Route path="" element={<TermService />} />
          </Route>
          <Route path={ROUTE_URLS.PORTAL_RULES} element={<PrivateRoute />}>
            <Route path="" element={<PortalRulesPage />} />
          </Route>
          <Route path={ROUTE_URLS.CONTACT_INFORMATION} element={<PrivateRoute />}>
            <Route path="" element={<ContactInformation />} />
          </Route>
          <Route path={ROUTE_URLS.WARRANTY_TERMS} element={<PrivateRoute />}>
            <Route path="" element={<WarrantyTermsPage />} />
          </Route>
          <Route path={ROUTE_URLS.WARRANTY_PERIODS} element={<PrivateRoute />}>
            <Route path="" element={<WarrantyPeriodsPage />} />
          </Route>
          <Route path={ROUTE_URLS.WARRANTY_CASE} element={<PrivateRoute />}>
            <Route path="" element={<WarrantyCasePage />} />
          </Route>

          <Route path={ROUTE_URLS.WARRANTIES} element={<PrivateRoute />}>
            <Route path="" element={<WarrantyRequest />} />
          </Route>
          <Route path={ROUTE_URLS.WARRANTY} element={<PrivateRoute />}>
            <Route path="" element={<WarrantyRequestPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Container>
  );
}

export default App;
